/**
 * - _formElement.js
 * - formElement-checkbox.js
 * - formElement-email.js
 * - 🚧 formElement-file.js
 * - 🚧 formElement-password.js
 * - formElement-radio.js
 * - formElement-select.js
 * - validation.js
 */

// import { extractParsedDataAttrs } from './dtl-syi';
import { trackGtmEvent } from '@gumtree/shared/src/util/gtm-tracker';
import { isUkPostcode } from '@gumtree/shared/src/util/postcode-validation';
import type { Form } from './form';
import type { SyiUseDispatch } from '../reducers';
import { isSyiAiPostOnExperiment, updateSyiFormError } from '../reducers/syi';

/** `FormElement` is base class of others */
export type FormElementType = FormElement | FormElementRadio;

/**
 * - `HTMLSelectElement` or
 * - `HTMLInputElement`: Checkbox, Email, File, Password, Radio
 */
export type UnderlyingFormElement = HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement;

export class FormElement<T extends UnderlyingFormElement = UnderlyingFormElement> {
    DEFAULTS = {
        validation: {
            /** Option to show a list of errors instead of one error at a time */
            showAllErrors: false,
            /** Add a custom class to the errors ul list if needed */
            errorsClass: undefined,
        },
    };

    DEFAULT_ERROR_MESSAGES: {
        isEmail: 'Please enter a valid email address.';
        isValidPassword: string;
        isUKPostCode: 'Please enter a valid UK post code.';
        isPhoneNumber: 'Please enter a valid phone number.';
        isNotEmpty: 'This field cannot be empty.';
        isNotEmptyCheckbox: 'Please check this box.';
        isNotEmptySelect: 'Please select an option.';
        isNotEmptyRadio: 'Please select an option.';
        isPairedValidation: 'Please select at least one of these options';
        isMaxLength: `The text cannot exceed ${string} characters.`;
        isMinLength: `Too short. Please enter at least ${string} characters.`;
        isMax: `Please enter a number not higher than ${string}.`;
        isMin: `Please enter a number not lower than ${string}.`;
        isNumber: 'Please enter a valid number.';
        isInteger: 'Please enter a whole number.';
        isInRange:
            | `Please enter a number between undefined and ${string}.`
            | `Please enter a number between ${string} and undefined.`
            | `Please enter a number between ${string} and ${string}.`;
        isValidFileType: 'This file type is not valid. Please choose another file.';
        isValidFileSize: 'The size of this file exceeds the maximum allowed. Please choose another file.';
        isEqual: 'Fields do not match.';
        isUrl: 'Please enter a valid URL';
        isRequiredPaired: 'You need to select one option.';
    };

    /**
     * Value of the element - boolean if its a checkbox
     */
    actualValue?: string | boolean;
    charCount?: boolean;
    clearInput?: any;

    /** We can have an element listening to another element */
    listenTo?: HTMLElement;
    listenToComponent?: FormElementType;

    /**
     * The min value of this element, gets the number from the a min attribute
     */
    min?: string;
    max?: string;
    minWords?: string;
    maxWords?: string;

    noPristine: boolean;

    /**
     * The element to be paired with
     */
    pairedEl?: HTMLElement[];

    /**
     * Value of the placeholder attribute
     */
    placeholder?: string;

    /**
     * An object that details the state of the input, with a defined default state
     */
    state: {
        name: string;
        currentValue: string;
        initialValue?: string;
        touched: boolean;
        pristine: boolean;
        valid?: boolean;
        errors: string[];
        submitted: boolean;
        noValidation?: boolean;
    };

    /** Name of the validation to broadcast to */
    validationGroup?: string;

    /**
     * An array of validation rules associated against this element
     * @type array
     */
    validationRules = [] as string[];

    /** First two args seem degenerate but match subclass pattern */
    constructor(
        public el: T,
        public form: Form,
        public options: {
            autosubmit?: string;
            broadcastValue?: string;
            charCount?: boolean;
            errorClass?: string;
            errorsClass?: string;
            pairedCopySubmit?: string;
            pairedRequired?: string;
            selectAll?: string;
            selectAllChannel?: string;
            syncInputs?: string;
            ignoreSyncValues?: (string | boolean)[];
            validation: Record<string, string>;
            validationDisableonvalue?: string;
            validationGroupChannel?: string;
            validationRequireonvalue?: string;
            validationRules?: string;
        } = { validation: {} },
        protected dispatch: SyiUseDispatch
    ) {
        this._setOptions();

        this.min = el.getAttribute('min') ?? undefined;
        this.max = el.getAttribute('max') ?? undefined;
        this.minWords = el.dataset.minWords;

        this.DEFAULT_ERROR_MESSAGES = {
            isEmail: 'Please enter a valid email address.',
            isValidPassword:
                'Include at least <strong>one capital letter</strong>, <strong>one lowercase letter</strong>, <strong>one number</strong> ' +
                'and <strong>one special character</strong><br> e.g. !@#$£%^*-_+=',
            isUKPostCode: 'Please enter a valid UK post code.',
            isPhoneNumber: 'Please enter a valid phone number.',
            isNotEmpty: 'This field cannot be empty.',
            isNotEmptyCheckbox: 'Please check this box.',
            isNotEmptySelect: 'Please select an option.',
            isNotEmptyRadio: 'Please select an option.',
            isPairedValidation: 'Please select at least one of these options',
            isMaxLength: `The text cannot exceed ${this.max} characters.`,
            isMinLength: `Too short. Please enter at least ${this.min} characters.`,
            isMax: `Please enter a number not higher than ${this.max}.`,
            isMin: `Please enter a number not lower than ${this.min}.`,
            isNumber: 'Please enter a valid number.',
            isInteger: 'Please enter a whole number.',
            isInRange: `Please enter a number between ${this.min} and ${this.max}.`,
            isValidFileType: 'This file type is not valid. Please choose another file.',
            isValidFileSize:
                'The size of this file exceeds the maximum allowed. Please choose another file.',
            isEqual: 'Fields do not match.',
            isUrl: 'Please enter a valid URL',
            isRequiredPaired: 'You need to select one option.',
        };

        this.noPristine = this.el.getAttribute('noPristine') === 'true';

        this.placeholder = this.el.getAttribute('placeholder') ?? undefined;

        this.state = {
            name: this.el.name,
            currentValue: this.el.value,
            initialValue: this.el instanceof HTMLInputElement ? this.el.defaultValue : undefined,
            touched: false,
            pristine: true,
            valid: undefined,
            errors: [],
            submitted: false,
        };
    }

    addError(message) {
        this.state.valid = false;
        this.state.errors.push(message);
        this.dispatch(
            updateSyiFormError({ elName: this.el.name, errors: this.state.errors.slice() })
        );
    }

    clear(focus?: boolean) {
        focus = focus !== false;

        if (this.el.type !== 'hidden') {
            this.el.value = '';
            const event = new Event('change');
            this.el.dispatchEvent(event);
            // this.countChars(); // 🚧
            if (this.el.type === 'radio') {
                const $checkedRadios = Array.from(
                    this.form.el.querySelectorAll('input[type="radio"]:checked')
                ) as HTMLInputElement[];
                const $defaultRadios = Array.from(
                    this.form.el.querySelectorAll('input[type="radio"][value=""]')
                ) as HTMLInputElement[];
                $checkedRadios.forEach((el) => {
                    el.checked = false;
                    el.removeAttribute('checked');
                });
                (this.el as HTMLInputElement).checked = false;
                this.el.removeAttribute('checked');
                $defaultRadios.forEach((x) => {
                    x.checked = true;
                    x.setAttribute('checked', 'checked');
                });
            }

            // Trigger content clone // 🚧
            // const contentClone = $(this.el).attr('data-content-clone');
            // if (contentClone !== undefined) {
            //     const radioChannel = `content-clone-cc-${ this.el.getAttribute('name')! }`;
            //     Gum.RADIO.trigger(radioChannel, { value: '' });
            // }

            this.ready();
            this.removeErrors();
            // this.RADIO.trigger('clear'); // 🚧 ClearInput?

            // the reset/clear functionality overirdes value="all" to empty, so need to set it back
            // TODO - Move into the clear radio
            if (this.el.name === 'search_category') {
                setTimeout(function () {
                    //$(this.el ).val('all');
                    (
                        Array.from(
                            document.querySelectorAll('[name="search_category"][value=""]')
                        ) as HTMLInputElement[]
                    ).forEach((x) => (x.value = 'all'));
                }, 100);
            }

            if (focus) {
                this.el.focus();
            }
        }
    }

    // ℹ️ not in legacy
    cleanup() {
        this.el.removeEventListener('blur', this.onBlur);
        // this.el.removeEventListener('click', this.onClick);
        this.el.removeEventListener('keyup', this.onKeyUp);
        this.el.removeEventListener('keydown', this.onKeyDown);
    }

    countChars() {
        const value = this.getValue();
        if (this.charCount && typeof value === 'string') {
            let charCount: number;
            if (this.max) {
                charCount = Number(this.max) - value.length;
            } else {
                charCount = value.length;
            }
            this.form.onCount[this.el.name]?.forEach((cb) => cb(charCount));
        }
    }

    customValidation() {
        // extended objects will override this
        return true; // return true by default
    }

    disable = () => {
        this.resetErrors();
        this.removeErrors();
        if (this.clearInput) {
            this.clearInput.hide();
        }
        this.el.disabled = true;
        this.form.removeElementGroup({
            name: this.validationGroup!,
            element: this,
        });
    };

    enable = () => {
        this.el.disabled = false;
        this.form.addElementGroup({
            name: this.validationGroup!,
            element: this,
        });
        if (this.clearInput) {
            this.clearInput.show();
        }

        this.isValid(undefined, true);
    };

    // 🚧 we changed placeHolder -> placeholder below
    preventPlaceholderValue() {
        // this will be called only on form's submit to prevent placeholder text being sent as a value
        if (!this.placeholder && this.el.getAttribute('placeholder') === this.el.value) {
            this.el.value = '';
        }
    }

    /** Get error message */
    getErrorMessage(
        /** Getting the global/custom message by type */
        type: string,
        /** Optional param to replace with in the message */
        value?: string | number
    ) {
        if (typeof value === 'undefined') {
            value = '';
        }

        const dataStr = `${type.toLowerCase()}Error`;
        const dataMsg = this.options.validation[dataStr];

        if (dataMsg) {
            // first check the data element
            return dataMsg.replace('%s', value.toString());
        } else if (typeof this.DEFAULT_ERROR_MESSAGES[type] !== 'undefined') {
            // if data element is not there, check the default ones
            return this.DEFAULT_ERROR_MESSAGES[type].replace('%s', value);
        } else {
            // if there is none in the default ones, return nothing
            return '';
        }
    }

    getValue() {
        return this.el instanceof HTMLInputElement && this.el.type === 'checkbox'
            ? this.el.checked
            : this.el.value;
    }

    isDisabled() {
        return this.el.disabled;
    }

    isErrorneous() {
        return this.el.classList.contains('is-invalid');
    }

    init() {
        // Check if it has a data-char-count=true
        if (typeof this.options.charCount !== 'undefined') {
            this.charCount = true;
        }

        this._setValidationConditions();

        this._addSubscriptions();

        this.setListenTo();

        this._addClearInput();

        this.countChars();

        if (
            this.dispatch(isSyiAiPostOnExperiment()) &&
            ['title', 'description'].includes(this.el.name) &&
            this.getValue() &&
            !this.isErrorneous()
        ) {
            // if title or description is not empty, validate the group
            this.form.validateElementGroup(this.validationGroup!, true);
        }
    }

    isEqualToMaster() {
        if (typeof this.listenToComponent === 'undefined') {
            return false;
        }
        return this.listenToComponent.getValue() === this.getValue();
    }

    isRequired() {
        return this.el.hasAttribute('required');
    }

    onAutoSubmit() {
        // NOOP
    }

    onBlur = (event: FocusEvent | Event) => {
        const channelName = this.el.name.replace('[', '_').replace(']', '_');
        // trigger element's change radio channel
        this.form.onChange[channelName]?.forEach((cb) => cb(this.getValue()));
        // 🚧 legacy does not actually send the value, so do we need to?
        this.form.onValue[`${channelName}.${this.getValue()}`]?.forEach((cb) =>
            cb(this.getValue())
        );
        this.state.touched = true;
        this.countChars();
        // trigger elements validation
        const state = this.isValid(undefined, true);
        // If onBlur is Send/Submit button then attempt submit
        const relatedTarget = 'relatedTarget' in event ? event.relatedTarget : undefined;
        if (
            this.form.name === 'reply-form-frm' &&
            relatedTarget &&
            'submit' === (relatedTarget as HTMLElement).getAttribute('type')
        ) {
            // 🚧
            // mobile-web-static-assets/src/main/resources/rwd/common-header-vendors.js
            // window.replyFormSubmit();
        }
        return state;
    };

    onChange = (_event: Event) => {
        // NOOP
    };

    onClick = () => {
        // NOOP
    };

    onKeyDown = () => {
        this.form.onChange[this.el.name]?.forEach((cb) => cb(this.getValue()));
        // trigger
        if (this.isErrorneous()) {
            this.isValid(false, true);
        }
    };

    onKeyUp = () => {
        this.state.currentValue = this.el.value;
        if (this.noPristine) {
            this.state.pristine = false;
        } else {
            this.state.pristine = this.state.currentValue === this.state.initialValue;
        }
        this.countChars();
    };

    ready() {
        this.el.disabled = false;
        // Gum.native.removeClass('is-waiting');
    }

    setValue(data: { [x: string]: any }) {
        this.el.value =
            typeof data[this.el.name] !== 'undefined' ? data[this.el.name] : this.el.value;
    }

    isValid(skipGroupValidation?: boolean, elementValidation?: boolean) {
        // const valueChannel = this.options.broadcastValue || this.el.name;
        const _valueChannel = this.el.name;

        if (
            !skipGroupValidation &&
            this.validationGroup
            // 🚧 need validation group because validateElementGroup has side-effect
            // this.form.validationGroup[this.validationGroup].length > 1
        ) {
            this.form.validateElementGroup(this.validationGroup, elementValidation);
        }

        // If validation has been fired by an input change or by
        // group validation check then pass through element validation
        const _isValid = this.validate(elementValidation);

        /**
         * 🚧 Description has `data-radio-content` listening for this event.
         * But it only seems to apply some formatting, so we skip for now.
         */
        // Gum.RADIO.trigger(
        //     `form.${  this.form.name  }.${  valueChannel}`,
        //     _isValid ? this.getValue() : ''
        // );

        return this.state;
    }

    removeErrors() {
        this.form.el.classList.remove('is-invalid');
        this.form.el.setAttribute('aria-invalid', 'false');
        return this;
    }

    resetErrors() {
        this.state.valid = undefined;
        this.state.errors = [];
        this.dispatch(updateSyiFormError({ elName: this.el.name, errors: [] }));
    }

    selectAllSetUp() {
        // NOOP
    }

    setListenTo() {
        const listenTo = this.options.validation.isequalListento;
        if (typeof listenTo !== 'undefined') {
            this.listenToComponent = this.form.getElementByName(listenTo);

            if (typeof this.listenToComponent !== 'undefined') {
                this.listenTo = this.listenToComponent.el;
            }
        }
    }

    showErrors(elementValidation?: boolean) {
        if (this.state.errors.length === 0) {
            return;
        }

        if (
            !this.state.submitted &&
            elementValidation &&
            (!this.state.touched || this.state.pristine)
        ) {
            this.dispatch(updateSyiFormError({ elName: this.el.name, shouldShow: false }));
            return;
        }

        this.el.dataset &&
            this.el.dataset.pagetype === 'reset-password' &&
            trackGtmEvent({ action: 'PasswordResetError' });

        this.el.classList.add('is-invalid');
        this.el.setAttribute('aria-invalid', 'true');

        this.dispatch(updateSyiFormError({ elName: this.el.name, shouldShow: true }));
    }

    // 🚧 Never invoked?
    showServerSideErrors(_response) {
        // Object.keys(response)
        //     .filter((error) => error.name === this.el.name)
        //     .forEach((error) => {
        //         this.resetErrors();
        //         this.removeErrors();
        //         this.addError(error.value);
        //         this.showErrors();
        //     });
    }

    syncInputs() {
        // NOOP
    }

    syncValue(_value: boolean) {
        // NOOP
    }

    validate(elementValidation?: boolean) {
        if (!elementValidation) {
            this.state.submitted = true;
        }

        this.actualValue = this.getValue();

        // We can assume if the value is the same as placeholder, that the value is actually an empty string
        if (this.placeholder === this.actualValue) {
            this.actualValue = '';
        }

        // first we reset errors
        this.resetErrors();

        // then we hide all errors
        this.removeErrors();

        // First check if the element is disabled
        if (this.isDisabled()) {
            // if it is, we shoudln't validate it
            return true;
        }

        // Next check if the field is required
        if (!this.isRequired() && !this.pairedEl) {
            // if it is, we need to return true if it's empty
            if (this.actualValue === '' || this.actualValue === false) {
                this.state.valid = true;
                this.state.noValidation = true;
                return true;
            }
        }

        // Check if there are any conditions set at all
        // If not, then add isNotEmpty
        // if (this.isRequired && this.validationRules.length === 0) {
        if (this.isRequired() && this.validationRules.length === 0) {
            this.validationRules.push('isNotEmpty');
        }

        // Check upon all the conditions
        this.validationRules.forEach((condition) => {
            const options = {
                min: this.min,
                max: this.max,
                minWords: this.minWords,
                paired: this.pairedEl,
            };

            const validateByType = () => {
                if (this.el.type === 'checkbox') {
                    if (this.actualValue === false) {
                        this.addError(this.getErrorMessage('isNotEmptyCheckbox'));
                    }
                } else if (this.el.type === 'select-one' || this.el.type === 'select-multiple') {
                    if (!validation.isNotEmpty(this.actualValue)) {
                        this.addError(this.getErrorMessage('isNotEmptySelect'));
                    }
                } else {
                    if (!validation.isNotEmpty(this.actualValue)) {
                        this.addError(this.getErrorMessage('isNotEmpty'));
                    }
                }
            };

            // Make sure that the condition actually exists
            if (typeof validation[condition] !== 'undefined') {
                if (condition === 'isNotEmpty') {
                    validateByType();
                } else {
                    if (!validation[condition](this.actualValue, options)) {
                        this.addError(this.getErrorMessage(condition));
                    }
                }
            } else if (condition === 'isEqual') {
                // start listening to the Master only here
                /**
                 * 🚧 channel ...changeMaster does not seem to be toggled anywhere?
                 */
                // Gum.RADIO.off(
                //     `form.${  self.form.name  }.${  self.listenTo.name  }.changeMaster`,
                //     self.validate
                // );
                // Gum.RADIO.on(
                //     `form.${  self.form.name  }.${  self.listenTo.name  }.changeMaster`,
                //     self.validate
                // );
                if (!this.isEqualToMaster()) {
                    this.addError(this.getErrorMessage(condition));
                }
            } else if (condition === 'isPairedValidation') {
                //TODO Currently this is not clever enough to clear errors from its pairel via a call
                // this.pairedEl?.forEach((el) =>
                //     el.parentElement?.querySelector('.error-messages')?.remove()
                // );

                const checked = (this.el as HTMLInputElement).checked;
                const paired = Array.from(this.pairedEl ?? []).some(
                    (el) => (el as HTMLInputElement).checked
                );

                if (!checked && !paired) {
                    this.addError(this.getErrorMessage(condition));
                    this.dispatch(updateSyiFormError({ elName: this.el.name, shouldShow: true }));
                }
            }
        });

        // Now, check the custom validation stuff first
        this.customValidation();

        // Check if there are errors in the array
        if (this.state.errors.length) {
            this.showErrors(elementValidation);
            this.state.valid = false;
            return false;
        }

        this.state.valid = true;

        return true;
    }

    // 🚧 Can we avoid instantiating ClearInput?
    // It creates clear icon and adds/removes CSS classes on events
    _addClearInput() {
        // const clearinput = this.el.dataset.clearinput;
        // if (clearinput !== 'false' && Gum.util.isTextbox(this.el)) {
        //     this.clearInput = new Gum.Components.ClearInput(this);
        // }
    }

    _addSubscriptions() {
        // 🚧
        // this.RADIO.on('isInvalid', this.showErrors);
        // this.form.RADIO.on('clearFields', this.clear);
        // Gum.RADIO.on(`form.${this.form.name}.serverErrors`, this.showServerSideErrors);
        this.form.onServerErrors.push((resp) => this.showServerSideErrors(resp));

        const events: ('change' | 'blur')[] =
            this.el.type === 'select-one' ||
            this.el.type === 'select-multiple' ||
            this.el.type === 'checkbox' ||
            this.el.type === 'radio' ||
            this.el.type === 'file'
                ? ['change', 'blur']
                : ['blur'];

        if (this.el.type === 'radio') {
            document
                .querySelectorAll(`[name='${this.el.name}']`)
                .forEach((el) =>
                    events.forEach((event) => el.addEventListener(event, this.onBlur))
                );
        } else {
            events.forEach((event) => this.el.addEventListener(event, this.onBlur));
        }

        this.el.addEventListener('click', this.onClick);

        this.el.addEventListener('keydown', this.onKeyDown);
        this.el.addEventListener('keyup', this.onKeyUp);

        if (this.el.type !== 'radio' && this.el.type !== 'checkbox') {
            this.el.addEventListener('paste', () => setTimeout(() => this.onKeyUp(), 1));
        }

        // 🚧
        // Gum.native.addEventListener(this.el, 'input', this.onKeyDown); // needed for some browsers
        // this.form.RADIO.on('update.fields', this.setValue);
    }

    _setOptions() {
        // First fetch element options set via data-validation-...
        // const validationOptions = extractParsedDataAttrs(this.el, 'data-validation-');
        const validationOptions = { ...this.el.dataset };
        const prefixLength = 'validation'.length;
        Object.keys(validationOptions).forEach((key) => {
            if (key.startsWith('validation')) {
                validationOptions[
                    `${key[prefixLength].toLowerCase()}${key.slice(prefixLength + 1)}`
                ] = validationOptions[key];
            }
            delete validationOptions[key];
        });

        // const elData = extractParsedDataAttrs(this.el);
        const elData = { ...this.el.dataset };

        // at the end, override with js set options
        this.options = Object.assign({}, this.DEFAULTS, elData, this.options);

        this.options.validation = Object.assign({}, this.options.validation, validationOptions);
    }

    _setValidationConditions() {
        let dep: string[], dep1: string;

        // Check if there are dependant elements
        if (typeof this.options.validationDisableonvalue !== 'undefined') {
            dep = this.options.validationDisableonvalue.split(',');
            dep1 = dep[0].replace('[', '_').replace(']', '_');

            // 🚧 Cleaner approach
            // 🚧 Migrate triggers (FormElementCheckbox)
            // - form.syi.{dep1}.change
            // Gum.RADIO.on('form.' + this.form.name + '.' + dep1 + '.change', this.enable);
            this.form.onChange[dep1] ||= [];
            this.form.onChange[dep1].find((x) => x === this.enable) ||
                this.form.onChange[dep1].push(this.enable);
            // - form.syi.{dep1}.value.{dep2}
            // Gum.RADIO.on('form.' + this.form.name + '.' + dep1 + '.value.' + dep[1], this.disable);
            this.form.onValue[`${dep1}.${dep[1]}`] ||= [];
            this.form.onValue[`${dep1}.${dep[1]}`].find((x) => x === this.disable) ||
                this.form.onValue[`${dep1}.${dep[1]}`].push(this.disable);
        }

        // Join any validation group
        if (typeof this.options.validationGroupChannel !== 'undefined') {
            this.validationGroup = this.options.validationGroupChannel;
            if (!this.isDisabled()) {
                this.form.addElementGroup({
                    name: this.validationGroup,
                    element: this,
                });
            }
        }

        // Join any validation group
        if (typeof this.options.pairedRequired !== 'undefined') {
            this.pairedEl = Array.from(
                this.form.el.querySelectorAll<HTMLElement>(`.${this.options.pairedRequired}`)
            );
        }

        // copies value of set form into itself
        // this is being used in the post ad new registration page where the contact form has been removed and the
        // contact telephone needs to be copied.
        if (typeof this.options.pairedCopySubmit !== 'undefined') {
            const copyFrom = this.form.el.querySelector<HTMLInputElement>(
                `[name=${this.options.pairedCopySubmit}]`
            );
            if (copyFrom) {
                // 🚧 Migrate triggers
                (this.form.onChange[this.options.pairedCopySubmit] ||= []).push(
                    () => (this.el.value = copyFrom.value)
                );
                this.form.onIsValid.push(() => (this.el.value = copyFrom.value));
            }
        }

        // Check if there are dependent elements
        if (typeof this.options.validationRequireonvalue !== 'undefined') {
            dep = this.options.validationRequireonvalue.split(',');
            dep1 = dep[0].replace('[', '_').replace(']', '_');

            // 🚧 Migrate triggers (FormElementCheckbox)
            // Gum.RADIO.on('form.' + this.form.name + '.' + dep1 + '.change', this.unrequire);
            // Gum.RADIO.on('form.' + this.form.name + '.' + dep1 + '.value.' + dep[1], this.require);
        }

        // populating this.CONDITIONS array from data-validation-conditions
        if (
            typeof this.options.validationRules === 'undefined' ||
            this.options.validationRules.length === 0
        ) {
            // try to check for conditions in main name space
            if (this.el.dataset.validation) {
                this.options.validationRules = this.el.dataset.validation;
            } else {
                return this;
            }
        }

        this.validationRules = this.options.validationRules.split(' ');

        return this; // return element back
    }
}

export class FormElementCheckbox extends FormElement<HTMLInputElement> {
    init() {
        super.init();

        const autoSubmit = this.options.autosubmit;

        // 🚧 Probably need to be refactored, added this because websiteUrl checkbox isn't linked to "Select All"
        if (this.el.name === 'features[WEBSITE_URL][selected]') {
            this.el.addEventListener('change', this.onChange);
        }

        if (autoSubmit || autoSubmit === '') {
            this.el.addEventListener('change', this.onAutoSubmit.bind(this));
        }

        if (this.options.selectAll || this.options.selectAllChannel) {
            this.el.addEventListener('change', this.onChange);
            this.selectAllSetUp();
        }

        if (this.options.syncInputs) {
            (this.form.onSync[this.options.syncInputs] ||= []).push((value) => {
                this.syncValue(value);
            });
            this.el.addEventListener('change', () => this.syncInputs());
        }
    }

    syncValue(value: boolean) {
        this.el.checked = value;
        if (this.options.selectAllChannel) {
            setTimeout(() => {
                if (this.form.onSelectAllChild[this.options.selectAllChannel!]) {
                    this.form.onSelectAllChild[this.options.selectAllChannel!].forEach((cb) =>
                        cb()
                    );
                }
            }, 1);
        }
    }

    syncInputs() {
        // if the value is in the ignoreSyncValues array, don't sync the change
        if (this.options.ignoreSyncValues?.includes(this.el.checked)) {
            return;
        }

        this.form.onSync[this.options.syncInputs!]?.forEach((cb) => {
            cb(this.el.checked);
            // 🚧 Why six events triggered instead of three on Select All!?
            this.form.onChange[this.el.name]?.forEach((cb) => cb(this.getValue()));
        });
    }

    selectAllSetUp() {
        if (this.options.selectAllChannel) {
            // this.form.RADIO.on( 'form.selectAll.' + this.options.selectAllChannel, this.selectAllCalled );
            (this.form.onSelectAll[this.options.selectAllChannel] ||= [])?.push((value) =>
                this.selectAllCalled(value)
            );
        }

        if (this.options.selectAll) {
            // this.form.RADIO.on( 'form.selectAll.' + this.options.selectAll + '.child', this.selectAllChild );
            (this.form.onSelectAllChild[this.options.selectAll] ||= [])?.push(() =>
                this.selectAllChild()
            );
        }
    }

    selectAll() {
        // this.form.RADIO.trigger( 'form.selectAll.' + this.options.selectAll, this.el.checked );
        this.form.onSelectAll[this.options.selectAll!]?.forEach((cb) => cb(this.el.checked));
    }

    selectAllChild() {
        const elements = Array.from(
            this.form.el.querySelectorAll(`[data-select-all-channel="${this.options.selectAll}"]`)
        ) as HTMLInputElement[];

        const selected = elements.some((el) => !el.checked) ? false : true;
        this.el.checked = selected;
    }

    selectAllCalled(value: boolean) {
        this.el.checked = value;
        if (this.options.syncInputs) {
            this.syncInputs();
        }
    }

    onChange = (event: Event) => {
        // Gum.RADIO.trigger( "form." + this.form.name + "." + this.el.name + '.change', this.getValue() );
        this.form.onChange[this.el.name]?.forEach((cb) => cb(this.getValue()));
        // Gum.RADIO.trigger( "form." + this.form.name + "." + this.el.name + '.value.' + this.getValue() );
        this.form.onValue[`${this.el.name}.${this.getValue()}`]?.forEach((cb) => cb(undefined));

        if (this.options.selectAll) {
            this.selectAll();
        }

        if (this.options.selectAllChannel) {
            // this.form.RADIO.trigger( 'form.selectAll.' + this.options.selectAllChannel + '.child',
            //     this.selectAllChild );
            this.form.onSelectAllChild[this.options.selectAllChannel]?.forEach((cb) =>
                cb(this.selectAllChild)
            );
        }

        if (event) {
            // We need to check if the blur was triggered by the submit button
            if (
                'relatedTarget' in event &&
                ((event as FocusEvent).relatedTarget as HTMLInputElement)?.type === 'submit'
            ) {
                // if it has, cancel all onBlur events and retrigger the click
                ((event as FocusEvent).relatedTarget as HTMLInputElement)?.click();
                return true;
            }
        }

        // trigger elements validation
        return this.isValid(undefined, true);
    };

    clear(focus) {
        focus = focus !== false;

        if (this.el.type !== 'hidden') {
            this.el.checked = false;
            this.ready();
            this.removeErrors();
            // this.RADIO.trigger('clear'); // 🚧
            if (focus) {
                this.el.focus();
            }
        }
    }

    // 🚧
    onAutoSubmit() {
        // Gum.RADIO.trigger( "form." + this.form.name + '.submit' );
    }
}

export class FormElementSelect extends FormElement<HTMLSelectElement> {
    init() {
        super.init();

        const autoSubmit = this.options.autosubmit;
        if (autoSubmit || autoSubmit === '') {
            this.el.addEventListener('change', this.onAutoSubmit);
        }

        this.el.addEventListener('change', this.onChange);
    }

    onAutoSubmit() {
        setTimeout(async () => {
            // Gum.RADIO.trigger( "form." + self.form.name + '.submit' );
            await this.form.submit();
        }, 150);
    }

    onChange = (_event: Event) => {
        // Gum.RADIO.trigger( "form." + this.form.name + "." + this.el.name + '.change', this.getValue() );
        this.form.onChange[this.el.name]?.forEach((cb) => cb(this.getValue()));
        // Gum.RADIO.trigger( "form." + this.form.name + "." + this.el.name + '.value.' + this.getValue() );
        this.form.onValue[`${this.el.name}.${this.getValue()}`]?.forEach((cb) => cb(undefined));
    };
}

export class FormElementRadio extends FormElement {
    constructor(
        public el: HTMLInputElement & { type: 'radio' },
        public form: Form,
        undefined,
        dispatch: SyiUseDispatch
    ) {
        super(el, form, undefined, dispatch);
    }

    customValidation() {
        // to validate the radio box we have to check if at least one is checked in this set
        // thinking of possibility that other radio input boxes (in other forms) can have the same name
        const name = this.el.name;

        const isValid = !!this.form.el.querySelector(`[name="${name}"]:checked`);

        if (!isValid) {
            this.addError(this.getErrorMessage('isNotEmptyRadio'));
        }

        return isValid;
    }
}

export class FormElementEmail extends FormElement {
    constructor(
        public el: HTMLInputElement & { type: 'email' },
        public form: Form,
        undefined,
        protected dispatch: SyiUseDispatch
    ) {
        super(el, form, undefined, dispatch);
    }

    customValidation(eventType?: string) {
        const isValid = validation.isEmail(this.el.value);

        if (eventType === 'blur' && this.el.value === '') {
            return true;
        }

        if (!isValid) {
            // reset the whole array and add this error message
            this.resetErrors();
            this.state.errors.push(this.getErrorMessage('isEmail'));
            this.dispatch(
                updateSyiFormError({
                    elName: this.el.name,
                    errors: this.state.errors.slice(),
                    shouldShow: true,
                })
            );
        }
        return isValid; //isValid;
    }
}

/**
 * See mobile-web-static-assets/src/main/resources/rwd/framework/js/utils/utils.validation.js
 */
export const validation = {
    checkExists(options, _other) {
        let doesExist = true;
        let strings = 0;

        if (typeof options === 'undefined') {
            return false;
        }

        Array.from(arguments).forEach(function (arg) {
            if (typeof arg === 'string' && typeof options[arg] === 'undefined') {
                doesExist = false;
            }

            if (typeof arg === 'string') {
                strings++;
            }
        });

        if (strings === 0) {
            doesExist = false;
        }

        return doesExist;
    },
    isEmail(value: string) {
        const regex = /(.+)@(.+){2,}\.(.+){2,}/;
        return regex.test(value);
    },
    isFloat(value: string | number) {
        const str = value.toString();
        value = str.replace(/,/g, '');

        if (!this.isNumber(value)) {
            return false;
        }

        value = parseFloat(value);

        const regex = /^[+]?[0-9]+(\.[0-9]+)?$/;
        // ℹ️ RegExp.test supports numeric parameter
        return regex.test(value as any);
    },
    isMax(value: string, options: any) {
        if (!this.checkExists(options, 'max')) {
            return true;
        }
        return parseFloat(value) <= parseFloat(options.max);
    },
    isMaxLength(value: string, options: any) {
        if (!validation.checkExists(options, 'max')) {
            return true;
        }
        return value.length <= options.max;
    },
    isMin(value: string, options: any) {
        if (!this.checkExists(options, 'min')) {
            return true;
        }
        return parseFloat(value) >= parseFloat(options.min);
    },
    isNotEmpty(value?: string | boolean | null) {
        return !(value === '' || value == null);
    },
    isNumber(value: string) {
        const str = value.toString();
        value = str.replace(/,/g, '');

        const validate =
            (Object.prototype.toString.call(value) === '[object Number]' ||
                Object.prototype.toString.call(value) === '[object String]') &&
            !isNaN(parseFloat(value)) &&
            // ℹ️ native `isFinite` supports string parameter
            isFinite(value.toString().replace(/^-/, '') as any);
        return validate;
    },
    isPhoneNumber(value: string) {
        return value === '' || /^[\d\s]+$/.test(value);
    },
    isUKPostCode(value) {
        return isUkPostcode(value);
    },
    isUrl(value: string) {
        const regex =
            /^(https?|ftp):\/\/(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(\#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|\/|\?)*)?$/i;
        return regex.test(value);
    },
};
